import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ClipboardList, Plus, Search, AlertTriangle, Clock, Wrench, Filter, Trash2, X } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";

const STATI = [
  { v: "aperta", label: "Aperta", color: "muted" },
  { v: "in_lavorazione", label: "In Lavorazione", color: "success" },
  { v: "in_attesa_ricambi", label: "Attesa Ricambi", color: "warning" },
  { v: "completata", label: "Completata", color: "success" },
  { v: "fatturata", label: "Fatturata", color: "muted" },
  { v: "chiusa", label: "Chiusa", color: "muted" },
];

const URGENZE = {
  bassa: { label: "Bassa", color: "muted" },
  media: { label: "Media", color: "warning" },
  alta: { label: "Alta", color: "warning" },
  critica: { label: "Critica", color: "danger" },
};

export default function WorkOrdersPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stato, setStato] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [extraMachines, setExtraMachines] = useState([]); // array of machine_id (additional)
  const [manualMode, setManualMode] = useState(false); // macchina manuale (non in elenco)
  const [form, setForm] = useState({ customer_id: "", machine_id: "", machine_manual_marca: "", machine_manual_modello: "", machine_manual_matricola: "", descrizione_problema: "", urgenza: "media", tipo_garanzia: "pagamento", tipologia_intervento: "officina", data_prevista: "", data_ingresso: "", preso_in_carico_da_id: "", note: "" });

  const load = async () => {
    const params = {};
    if (stato) params.stato = stato;
    if (q) params.q = q;
    if (sp.get("customer")) params.customer_id = sp.get("customer");
    const { data } = await api.get("/work-orders", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stato]);
  useEffect(() => { api.get("/customers").then(r=>setCustomers(r.data)); }, []);
  useEffect(() => { api.get("/users").then(r=>setUsers(r.data)).catch(()=>{}); }, []);

  useEffect(() => {
    if (form.customer_id) api.get("/machines", { params: { customer_id: form.customer_id } }).then(r=>setMachines(r.data));
    else setMachines([]);
  }, [form.customer_id]);

  const submit = async (e) => {
    e.preventDefault();
    // Modalità manuale: nessun elenco macchine, un solo WO
    if (manualMode) {
      if (!form.machine_manual_marca?.trim() || !form.machine_manual_modello?.trim()) {
        toast.error("Compila marca e modello della macchina manuale");
        return;
      }
      try {
        const { data } = await api.post("/work-orders", { ...form, machine_id: "" });
        toast.success(`Scheda ${data.numero} creata`);
        nav(`/schede/${data.id}`);
      } catch (err) {
        console.error("WO manual create", err.response?.status, err.response?.data);
        toast.error(extractApiError(err, "Impossibile creare la scheda"));
      }
      return;
    }
    // Compone la lista completa di macchine (principale + extra), dedup
    const allMachineIds = Array.from(new Set([form.machine_id, ...extraMachines].filter(Boolean)));
    if (allMachineIds.length === 0) { toast.error("Seleziona almeno una macchina"); return; }

    if (allMachineIds.length === 1) {
      try {
        const { data } = await api.post("/work-orders", { ...form, machine_manual_marca:"", machine_manual_modello:"", machine_manual_matricola:"" });
        toast.success(`Scheda ${data.numero} creata`);
        nav(`/schede/${data.id}`);
      } catch (err) {
        console.error("WO create error", err.response?.status, err.response?.data);
        toast.error(extractApiError(err, "Impossibile creare la scheda"));
      }
      return;
    }

    // Caso multiplo: crea N schede (una per macchina) con gli stessi dati
    const results = { ok: 0, fail: 0, first: null, errors: [] };
    for (const mid of allMachineIds) {
      try {
        const { data } = await api.post("/work-orders", { ...form, machine_id: mid, machine_manual_marca:"", machine_manual_modello:"", machine_manual_matricola:"" });
        results.ok += 1;
        if (!results.first) results.first = data;
      } catch (err) {
        console.error("WO multi create error", err.response?.status, err.response?.data);
        results.fail += 1;
        results.errors.push(extractApiError(err));
      }
    }
    if (results.ok > 0) toast.success(`${results.ok} schede create` + (results.fail ? ` (${results.fail} falliti)` : ""));
    if (results.fail > 0) toast.error(`Errori: ${results.errors.slice(0,2).join(" · ")}`);
    if (results.first) nav(`/schede`);
  };

  const addExtraMachine = () => setExtraMachines(arr => [...arr, ""]);
  const setExtraMachineAt = (i, v) => setExtraMachines(arr => arr.map((x, idx) => idx === i ? v : x));
  const removeExtraMachine = (i) => setExtraMachines(arr => arr.filter((_, idx) => idx !== i));

  const statoBadge = (s) => {
    const cfg = STATI.find(x=>x.v===s) || { label: s, color: "muted" };
    return <span className={`gm-badge gm-badge-${cfg.color}`}>{cfg.label}</span>;
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Assistenza tecnica</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Schede Riparazione</h1>
        </div>
        <button onClick={()=>setCreating(!creating)} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="wo-new-button">
          <Plus size={16} /> Nuova scheda
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="gm-card p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <SearchableSelect
            required
            testId="wo-customer"
            value={form.customer_id}
            onChange={(v)=>{ setForm(s=>({...s, customer_id: v, machine_id: ""})); setExtraMachines([]); }}
            placeholder="— Cliente * (scrivi per cercare)"
            options={customers.map(c => ({ value: c.id, label: c.ragione_sociale, subLabel: c.piva || c.referente || "" }))}
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Macchina *</label>
              <button
                type="button"
                onClick={()=>{ setManualMode(!manualMode); setForm(s=>({...s, machine_id:"", machine_manual_marca:"", machine_manual_modello:"", machine_manual_matricola:""})); setExtraMachines([]); }}
                className="text-[10px] uppercase tracking-widest font-bold text-[#FF9F0A] hover:underline"
                data-testid="wo-machine-manual-toggle"
              >
                {manualMode ? "← Usa elenco macchine" : "Macchina non in elenco →"}
              </button>
            </div>
            {!manualMode ? (
              <>
                <SearchableSelect
                  required
                  testId="wo-machine"
                  value={form.machine_id}
                  onChange={(v)=>setForm(s=>({...s, machine_id: v}))}
                  disabled={!form.customer_id}
                  placeholder={form.customer_id ? "— Macchina * (scrivi per cercare)" : "Seleziona prima un cliente"}
                  options={machines.map(m => ({ value: m.id, label: `${m.marca} ${m.modello}`, subLabel: m.matricola }))}
                />
                {extraMachines.map((mid, i) => (
                  <div key={i} className="mt-2 flex gap-2 items-start">
                    <div className="flex-1">
                      <SearchableSelect
                        testId={`wo-machine-extra-${i}`}
                        value={mid}
                        onChange={(v)=>setExtraMachineAt(i, v)}
                        disabled={!form.customer_id}
                        placeholder="— Macchina aggiuntiva"
                        options={machines.map(m => ({ value: m.id, label: `${m.marca} ${m.modello}`, subLabel: m.matricola }))}
                      />
                    </div>
                    <button type="button" onClick={()=>removeExtraMachine(i)} className="h-11 w-11 border border-zinc-700 hover:border-[#FF3B30] hover:text-[#FF3B30] flex items-center justify-center shrink-0" data-testid={`wo-machine-extra-remove-${i}`} title="Rimuovi"><X size={14}/></button>
                  </div>
                ))}
                {form.machine_id && (
                  <button type="button" onClick={addExtraMachine} disabled={!form.customer_id} className="mt-2 text-xs uppercase tracking-widest font-bold text-[#4CAF50] hover:underline flex items-center gap-1" data-testid="wo-add-machine">
                    <Plus size={12}/> Aggiungi altra macchina (stessa data e problema)
                  </button>
                )}
              </>
            ) : (
              <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-2 rounded-sm space-y-2" data-testid="wo-manual-machine">
                <div className="text-[10px] text-[#FF9F0A] uppercase tracking-widest">Dati macchina manuali · non salva in elenco</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input required={manualMode} placeholder="Marca *" value={form.machine_manual_marca} onChange={e=>setForm(s=>({...s, machine_manual_marca: e.target.value}))} className="gm-input" data-testid="wo-machine-manual-marca" />
                  <input required={manualMode} placeholder="Modello *" value={form.machine_manual_modello} onChange={e=>setForm(s=>({...s, machine_manual_modello: e.target.value}))} className="gm-input" data-testid="wo-machine-manual-modello" />
                  <input placeholder="Matricola" value={form.machine_manual_matricola} onChange={e=>setForm(s=>({...s, machine_manual_matricola: e.target.value}))} className="gm-input font-mono-data" data-testid="wo-machine-manual-matricola" />
                </div>
              </div>
            )}
          </div>
          <textarea required rows="3" placeholder="Descrizione problema riferita dal cliente *" value={form.descrizione_problema} onChange={e=>setForm(s=>({...s,descrizione_problema:e.target.value}))} className="gm-input py-2 h-auto resize-none md:col-span-2" data-testid="wo-desc" />
          <select value={form.urgenza} onChange={e=>setForm(s=>({...s,urgenza:e.target.value}))} className="gm-input">
            {Object.entries(URGENZE).map(([k,v])=><option key={k} value={k}>Urgenza: {v.label}</option>)}
          </select>
          <select value={form.tipologia_intervento} onChange={e=>setForm(s=>({...s,tipologia_intervento:e.target.value}))} className="gm-input" data-testid="wo-tipologia">
            <option value="officina">Tipologia: Officina</option>
            <option value="cantiere">Tipologia: Cantiere</option>
            <option value="emergenza">Tipologia: Emergenza</option>
          </select>
          <select value={form.tipo_garanzia} onChange={e=>setForm(s=>({...s,tipo_garanzia:e.target.value}))} className="gm-input">
            <option value="pagamento">A Pagamento</option>
            <option value="garanzia_cliente">Garanzia Cliente</option>
            <option value="garanzia_costruttore">Garanzia Costruttore</option>
          </select>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data prevista intervento</label>
            <input type="date" value={form.data_prevista} onChange={e=>setForm(s=>({...s,data_prevista:e.target.value}))} className="gm-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data ingresso macchina</label>
            <input type="date" value={form.data_ingresso} onChange={e=>setForm(s=>({...s,data_ingresso:e.target.value}))} className="gm-input" data-testid="wo-data-ingresso" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Preso in carico da</label>
            <SearchableSelect
              testId="wo-preso-in-carico"
              value={form.preso_in_carico_da_id}
              onChange={(v)=>setForm(s=>({...s, preso_in_carico_da_id: v}))}
              placeholder="— Chi ha preso in carico"
              options={users.map(u => ({ value: u.id, label: u.name, subLabel: u.role }))}
            />
          </div>
          <input placeholder="Note (opzionale)" value={form.note} onChange={e=>setForm(s=>({...s,note:e.target.value}))} className="gm-input md:col-span-2" />
          <button type="submit" className="gm-btn-primary h-12 rounded-sm md:col-span-2" data-testid="wo-submit">
            {manualMode
              ? "Crea scheda (macchina manuale)"
              : extraMachines.filter(Boolean).length > 0
                ? `Crea ${1 + extraMachines.filter(Boolean).length} schede (una per macchina)`
                : "Crea scheda"}
          </button>
        </form>
      )}

      <div className="gm-card p-3 mb-4 flex gap-2 flex-wrap items-center">
        <Filter size={14} className="text-zinc-500 mx-2" />
        <button onClick={()=>setStato("")} className={`px-3 h-9 border uppercase text-xs tracking-widest font-bold ${stato===""?"bg-[#4CAF50] text-white border-[#4CAF50]":"border-zinc-700 text-zinc-400"}`} data-testid="wo-filter-all">Tutte</button>
        {STATI.map(s=>(
          <button key={s.v} onClick={()=>setStato(s.v)} className={`px-3 h-9 border uppercase text-xs tracking-widest font-bold ${stato===s.v?"bg-[#4CAF50] text-white border-[#4CAF50]":"border-zinc-700 text-zinc-400"}`} data-testid={`wo-filter-${s.v}`}>
            {s.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px] ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} placeholder="Cerca per numero, cliente, macchina..." className="gm-input pl-10 h-10 text-sm" />
        </div>
      </div>

      <div className="gm-card overflow-x-auto">
        <table className="gm-table">
          <thead><tr><th>Numero</th><th>Data</th><th>Cliente</th><th>Macchina</th><th>Problema</th><th>Urgenza</th><th>Stato</th><th>Fattura</th><th className="text-right">€</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan="10" className="text-center text-zinc-500 py-8">Nessuna scheda</td></tr>)}
            {items.map(w => (
              <tr key={w.id} className="cursor-pointer" onClick={()=>nav(`/schede/${w.id}`)} data-testid="wo-row">
                <td className="font-mono-data text-[#4CAF50] font-bold">{w.numero}</td>
                <td className="text-xs text-zinc-400 font-mono-data">{new Date(w.created_at).toLocaleDateString("it-IT")}</td>
                <td className="text-sm">{w.customer_name}</td>
                <td className="text-xs text-zinc-300">{w.machine_label}</td>
                <td className="text-xs text-zinc-400 max-w-[280px] truncate">{w.descrizione_problema}</td>
                <td>
                  {URGENZE[w.urgenza] && <span className={`gm-badge gm-badge-${URGENZE[w.urgenza].color}`}>{URGENZE[w.urgenza].label}</span>}
                </td>
                <td>{statoBadge(w.stato)}</td>
                <td className="text-xs" data-testid="wo-row-fattura">
                  {w.numero_fattura ? <span className="font-mono-data text-[#4CAF50]">n° {w.numero_fattura}</span>
                    : w.fatturato ? <span className="gm-badge gm-badge-success">FATT.</span>
                    : <span className="text-zinc-600">—</span>}
                </td>
                <td className="text-right font-mono-data">€ {(w.totale||0).toFixed(2)}</td>
                <td className="text-right" onClick={e=>e.stopPropagation()}>
                  {(user?.role === "admin" || user?.role === "ufficio_acquisti") && (
                    <button onClick={async ()=>{
                      if (!window.confirm(`Eliminare scheda ${w.numero}?\n\nQUESTA OPERAZIONE È IRREVERSIBILE.`)) return;
                      try { await api.delete(`/work-orders/${w.id}`); toast.success("Eliminata"); load(); }
                      catch (err) { toast.error(extractApiError(err)); }
                    }} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1.5" data-testid="wo-row-delete" title="Elimina scheda">
                      <Trash2 size={14}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
